import React, { useMemo, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	DialogContent,
	Stack,
	Box,
	TextField,
	FormHelperText,
	Grid,
	FormLabel,
	FormControl,
	Switch,
} from '@mui/material';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import {
	useAddPoliciesToDealMutation,
	useCreateDebtorMutation,
	useGetDealPoliciesQuery,
	useUpdateDealRowMutation,
} from '@/features/deals/dealsApi';
import { InsightClaimTable } from '@/components/tables/InsightClaimTable';
import {
	useRetrieveFirebaseClaimStatusQuery,
	useUpdateInsightClaimMutation,
} from '@/features/claims/ticketsApi';
import {
	useCreateActivityMutation,
	useCreateNoteMutation,
	useDeleteNoteMutation,
} from '@/features/engagements/engagementsApi';
import { closeDialog } from '@/features/dialog/dialogSlice';
import { showToast } from '@/features/toast/toastSlice';
import { paths } from '@/app/routes';
import { useUpdateClientxMutation } from '@/features/clients/clientsApi';
import { useObjectContext } from '@/hooks/useObjectContext';
import { usePipelineContext } from '@/context/pipelineContext';
import { useGeneratePdfMutation } from '@/features/engagements/engagementsApi';
import { useAddFileToSharepointMutation } from '@/features/msGraph/msGraphApi';
import { usePoliciesSelect } from '@/context/policiesContext';
import CompleteDealStepsToComplete from './CompleteDealStepsToComplete';
import { CompleteDealRecordKeeping } from './CompleteDealRecordKeeping';
import CompleteDealHeader from '@/components/dialogs/CompleteDealDialog/CompleteDealHeader';
import { getPipelineName } from '@/components/dialogs/CompleteDealDialog/data';
import ConfirmCompletionDialog from '@/components/dialogs/CompleteDealDialog/ConfirmCompletionDialog';
import CompleteDialogActions from '@/components/dialogs/CompleteDealDialog/CompleteDialogActions';
import { CompletePolicySelection } from '@/components/dialogs/CompleteDealDialog/CompletePolicySelection';
import CompleteDealCreateDebtor from '@/components/dialogs/CompleteDealDialog/CompleteDealCreateDebtor';
import FolioCheckbox from '@/components/FolioCheckbox';

export const CompleteDealContent = ({ setLoading, loading }) => {
	const dispatch = useDispatch();

	const [savePolicyDocuments, setSavePolicyDocuments] = useState(true);
	const [createDebtor, setCreateDebtor] = useState(false);
	const [debtorDefaults, setDebtorDefaults] = useState(null);

	const { username: email } = useSelector((state) => state.msalAccount.account);

	const [leftPaneOpen, setLeftPaneOpen] = useState(false);

	const [additionalContent, setAdditionalContent] = useState('');
	const [confirmationOpen, setConfirmationOpen] = useState();
	const { selectedPolicies, clearSelectedPolicies, touched, setTouched } = usePoliciesSelect();

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const [addFileToSharepoint] = useAddFileToSharepointMutation();

	const [generatePdfMutation] = useGeneratePdfMutation();

	const [error, setError] = useState(false);

	const { clientRow, dealRow, dealId } = useObjectContext();

	const isNewBusiness = dealRow?.pipeline === process.env.REACT_APP_PIPELINE_NEW_BUSINESS;
	const isClaim = dealRow?.pipeline === process.env.REACT_APP_PIPELINE_CLAIMS;
	const isEndorsement = dealRow?.pipeline === process.env.REACT_APP_PIPELINE_ENDORSEMENTS;
	const isDebtor = dealRow?.pipeline === process.env.REACT_APP_PIPELINE_DEBTORS;
	const isRenewal = dealRow?.pipeline === process.env.REACT_APP_PIPELINE_RENEWALS;

	const isPolicyUploadDeal = isNewBusiness || isEndorsement || isRenewal;

	const { data: policyData, ...policiesQuery } = useGetDealPoliciesQuery(
		{ dealId },
		{ skip: !dealId },
	);

	useEffect(() => {
		if (touched && isPolicyUploadDeal) {
			setError(selectedPolicies.length === 0);
		}
	}, [selectedPolicies, touched, isPolicyUploadDeal]);

	const policies = policyData?.policies ?? [];

	const { pipeline, stages } = usePipelineContext(dealRow?.pipeline);

	const currentStage = stages.find((s) => s.id === dealRow?.dealStage);

	//some brokerages have underscores so making sure I only grab the prefix at the start
	const { data: claimStatuses } = useRetrieveFirebaseClaimStatusQuery(
		{ ticketId: dealId, ledger: userDetails?.broker?.ledger.split('__')[0].trim() },
		{ skip: !userDetails?.broker?.ledger },
	);

	const isEndorsementStage = useMemo(() => currentStage?.isEndorsement, [currentStage]);

	const pipelineName = useMemo(
		() => getPipelineName(pipeline, isEndorsementStage),
		[pipeline, isEndorsementStage],
	);

	const finalStage = useMemo(() => stages.find((s) => s.completed), [stages]);

	const [updateDealRow] = useUpdateDealRowMutation();

	const [updateInsightClaim] = useUpdateInsightClaimMutation();

	const [updateClientx] = useUpdateClientxMutation();
	const [createActivity] = useCreateActivityMutation();

	const [addPoliciesToDeal, { isLoadingPolicies }] = useAddPoliciesToDealMutation();
	const [createDebtorDeal] = useCreateDebtorMutation();

	const [createNote] = useCreateNoteMutation();
	const [deleteNote] = useDeleteNoteMutation();

	const updateDealStage = async (noteId) => {
		const dealRowUpdate = await updateDealRow({
			dealId,
			properties: {
				dealStage: finalStage.id,
				completed: true,
				closed: true,
				dateClosed: new Date().getTime(),
				completedBy: userDetails?.userId,
				closedBy: userDetails?.userId,
				...(noteId != null && { completionNoteId: noteId }),
			},
		}).unwrap();
		console.log('updated deal response: ', dealRowUpdate);
	};

	const updateClientType = async () => {
		const wasProspect = Boolean(clientRow?.isProspect);
		await updateClientx({
			clientId: clientRow?.clientId,
			insight: {
				ClientTypeId: 0,
			},
			firebase: {},
		}).unwrap();
		if (wasProspect) {
			await createActivity({
				clientId: clientRow?.id,
				activityType: 'prospectToClient',
			}).unwrap();
		}
	};

	const updateDealPolicies = async (policiesToAdd) => {
		await addPoliciesToDeal({
			dealId,
			clientId: clientRow?.clientId,
			policies: policiesToAdd,
			uploadPolicyDocs: isPolicyUploadDeal && savePolicyDocuments,
		});
	};

	const completeClaim = async (noteId) => {
		const closedStatus = claimStatuses.find((stage) => stage.isClosedStage);
		await updateDealStage(noteId);
		await updateInsightClaim({
			dealId,
			properties: {
				StatusId: closedStatus.insightStageId,
			},
		});
	};

	const handleCloseDialog = () => {
		setConfirmationOpen(false);
		dispatch(closeDialog('completeDeal'));
		clearSelectedPolicies();
	};

	const completeDeal = async () => {
		setLoading(true);
		let noteData;

		// If there’s additional content, create a note + PDF
		if (additionalContent?.trim()) {
			noteData = await createNewNote();
		}

		try {
			switch (dealRow?.pipeline) {
				case process.env.REACT_APP_PIPELINE_NEW_BUSINESS:
				case process.env.REACT_APP_PIPELINE_RENEWALS:
				case process.env.REACT_APP_PIPELINE_ENDORSEMENTS: {
					const policiesToAdd = selectedPolicies.map((policy) => ({
						policyId: policy.PolicyId,
						policyTransId: policy.Id,
					}));

					await updateDealPolicies(policiesToAdd);
					await updateDealStage(noteData?.id);
					if (dealRow?.pipeline === process.env.REACT_APP_PIPELINE_NEW_BUSINESS) {
						await updateClientType();
					}
					break;
				}

				case process.env.REACT_APP_PIPELINE_DEBTORS: {
					await updateDealRow({
						dealId: dealRow?.dealId,
						properties: {
							dealStage: finalStage?.stageId,
						},
					}).unwrap();

					break;
				}

				case process.env.REACT_APP_PIPELINE_CLAIMS: {
					await completeClaim(noteData?.id);
					break;
				}

				default: {
					await updateDealStage(noteData?.id);

					break;
				}
			}

			// upload note
			if (noteData?.subject && noteData?.body) {
				await uploadNote(noteData);
			}

			let createdDebtorId;
			if (createDebtor && debtorDefaults) {
				try {
					const debtorResponse = await createDebtorDeal({
						ownerDocId: debtorDefaults.ownerDocId,
						dealName: debtorDefaults.dealName,
						notes: '',
						clientId: clientRow?.clientId,
						completedBy: userDetails.userId,
						yearFolder: debtorDefaults.yearFolder,
						transactions: debtorDefaults.transactions,
					}).unwrap();
					createdDebtorId = debtorResponse?.debtorId;
				} catch (debtorErr) {
					console.error('❌ Error creating debtor', debtorErr);
					dispatch(
						showToast({
							message:
								debtorErr?.data?.message ||
								debtorErr.message ||
								'Error creating debtor',
							error: true,
						}),
					);
				}
			}

			dispatch(
				showToast({
					message: createdDebtorId
						? `${pipelineName} completed and debtor created!`
						: `${pipelineName} completed!`,
					action: createdDebtorId
						? {
								path: `${paths.debtors}/${createdDebtorId}`,
								label: 'GO TO DEBTOR',
							}
						: {
								path: `${paths.clients}/${clientRow?.clientId}`,
								label: 'GO TO CLIENT',
							},
				}),
			);
		} catch (err) {
			console.log('❌ Error adding policies', err);

			dispatch(
				showToast({
					message: err.message || 'Error adding policies',
					error: true,
				}),
			);
			if (noteData?.id) {
				await deleteNote({
					clientId: clientRow?.clientId,
					dealId,
					noteId: noteData.id,
					objectType: dealRow?.objectType,
				});
			}
		} finally {
			handleCloseDialog();
		}
	};

	const uploadNote = async ({ body, subject }) => {
		//Generate PDF from note HTML if tenant hasn't consented
		if (!userDetails?.tenantConsented) {
			const { blobUrl } = await generatePdfMutation({
				htmlContent: body,
				fileName: subject,
			}).unwrap();

			const blob = await fetch(blobUrl).then((r) => r.blob());
			const file = new File([blob], `${subject}.pdf`, {
				type: 'application/pdf',
			});

			if (dealRow?.notesDriveId) {
				try {
					await addFileToSharepoint({
						driveId: dealRow?.clientFolderDriveId,
						itemId: dealRow?.notesDriveId,
						file,
					});
					console.log('file uploaded to sharepoint successfully');
				} catch (err) {
					console.error('Error uploading file to SharePoint:', err);
				}
			} else {
				console.warn('No noteDriveId found, skipping SharePoint upload');
			}
		}
	};

	const createNewNote = async () => {
		const finalSubject = `${pipelineName} Note - ${new Date()
			.toISOString()
			.replace(/[:]/g, '.')}`;

		const noteBody = additionalContent.trim();

		const noteRes = await createNote({
			clientId: clientRow?.clientId,
			dealId,
			noteBody,
			objectType: dealRow?.objectType,
			subject: finalSubject,
		}).unwrap();

		return { body: noteBody, subject: finalSubject, id: noteRes.noteId };
	};

	return (
		<>
			<CompleteDealHeader />
			<DialogContent
				sx={{
					...(leftPaneOpen && {
						overflow: 'hidden',
						// height: '50vh',
						height: 'calc(90vh - 220px)',
						minHeight: 0,
					}),
				}}
			>
				<Stack
					spacing={3}
					sx={{
						...(!leftPaneOpen && { pt: '1em' }),
						height: '100%',
						minHeight: 0,
					}}
				>
					<Grid
						container
						spacing={leftPaneOpen ? 3 : 0}
						sx={{
							...(leftPaneOpen && {
								flex: 1,
								minHeight: 0,
								overflow: 'hidden',
								pt: 1.5,
							}),
						}}
					>
						<Grid
							size={4}
							sx={{
								height: '100%',
								minHeight: 0,
								display: 'flex',
								flexDirection: 'column',
								overflow: 'hidden',
							}}
						>
							{/* Stays pinned at the top; must not share the steps scrollport */}
							<Box sx={{ flexShrink: 0, pb: 1 }}>
								<CompleteDealRecordKeeping />
							</Box>
							{/* Steps stays mounted so it can set leftPaneOpen; only this region scrolls */}
							<Stack
								sx={{
									...(leftPaneOpen
										? {
												flex: 1,
												minHeight: 0,
												overflowY: 'auto',
												overflowX: 'hidden',
												pr: 1,
											}
										: { display: 'none' }),
								}}
							>
								<CompleteDealStepsToComplete setOpen={setLeftPaneOpen} />
							</Stack>
						</Grid>

						<Grid
							size={'grow'}
							sx={{
								...(leftPaneOpen && {
									height: '100%',
									minHeight: 0,
									display: 'flex',
									flexDirection: 'column',
								}),
							}}
						>
							<Stack
								spacing={1}
								sx={{
									...(leftPaneOpen && {
										flex: 1,
										minHeight: 0,
										height: '100%',
									}),
								}}
							>
								<Stack
									sx={{
										...(leftPaneOpen && {
											flex: 1,
											minHeight: 0,
											overflow: 'hidden',
										}),
									}}
								>
									<CompletePolicySelection
										{...{ setLoading, error }}
										fillAvailable={leftPaneOpen}
									/>
									{/* <PolicySelectionContent /> */}

									{isPolicyUploadDeal && (
										<FormHelperText {...{ error }}>
											{`Please select the ${
												isEndorsement
													? 'endorsement'
													: isRenewal
														? 'policy transaction'
														: 'policies'
											} related to this deal`}
										</FormHelperText>
									)}
								</Stack>
								{isPolicyUploadDeal && (
									<Grid
										container
										spacing={1.5}
										sx={{
											'& .MuiFormControlLabel-root': {
												minHeight: '40px',
											},
										}}
									>
										<Grid size={{ xs: 12, md: 6 }}>
											<FolioCheckbox
												component={Switch}
												controlProps={{ size: 'small' }}
												checked={savePolicyDocuments}
												disabled={loading}
												onChange={(e) =>
													setSavePolicyDocuments(e.target.checked)
												}
												label='Save policy documents to Sharepoint'
											/>
										</Grid>
										<CompleteDealCreateDebtor
											createDebtor={createDebtor}
											setCreateDebtor={setCreateDebtor}
											setDebtorDefaults={setDebtorDefaults}
											loading={loading}
										/>
									</Grid>
								)}
								<FormControl
									sx={{ flexShrink: 0, '& .MuiInputBase-root': { height: 35 } }}
								>
									<FormLabel>Note</FormLabel>
									<TextField
										fullWidth
										value={additionalContent}
										onChange={(e) => setAdditionalContent(e.target.value)}
										helperText={'This information will be added as a note.'}
									/>
								</FormControl>
							</Stack>
						</Grid>
					</Grid>
				</Stack>
			</DialogContent>
			<CompleteDialogActions
				disabled={loading || isLoadingPolicies || error}
				loading={loading || finalStage?.id === null || isLoadingPolicies}
				onCancel={handleCloseDialog}
				onConfirm={() => {
					setTouched(true);
					if (selectedPolicies.length === 0 && isPolicyUploadDeal) {
						setError(true);
						return;
					} else {
						setError(false);
					}
					setConfirmationOpen(true);
				}}
			/>
			{confirmationOpen && (
				<ConfirmCompletionDialog
					open={confirmationOpen}
					onClose={() => setConfirmationOpen(false)}
					onComplete={completeDeal}
					loading={loading}
					disabled={loading || isLoadingPolicies}
					pipelineName={pipelineName}
				/>
			)}
		</>
	);
};
